import { useState } from "react";

interface CreateIdeaModalProps {
  onClose: () => void;
  onSubmit: (title: string, content: string) => Promise<void>;
}

export const CreateIdeaModal = ({ onClose, onSubmit }: CreateIdeaModalProps) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim() || !content.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmit(title.trim(), content.trim());
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md bg-pk-purple-1 rounded-xl border-4 border-pk-purple-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b-2 border-pk-purple-3">
          <h2 className="text-pk-gray-5 font-bold text-lg">💡 Propose an Idea</h2>
          <button
            onClick={onClose}
            className="text-pk-gray-4 hover:text-pk-gray-5 text-xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Form */}
        <div className="p-4 flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-pk-gray-4 text-sm font-bold">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              placeholder="Short, descriptive title"
              className="w-full px-3 py-2 rounded-lg bg-pk-purple-2 border-2 border-pk-purple-3 text-pk-gray-5 placeholder:text-pk-gray-3 focus:outline-none focus:border-pk-purple-4"
            />
            <span className="text-pk-gray-3 text-xs text-right">
              {title.length}/100
            </span>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-pk-gray-4 text-sm font-bold">
              Description
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={1000}
              rows={5}
              placeholder="Describe your idea in detail. What problem does it solve? How would it work?"
              className="w-full px-3 py-2 rounded-lg bg-pk-purple-2 border-2 border-pk-purple-3 text-pk-gray-5 placeholder:text-pk-gray-3 focus:outline-none focus:border-pk-purple-4 resize-none"
            />
            <span className="text-pk-gray-3 text-xs text-right">
              {content.length}/1000
            </span>
          </div>

          <div className="flex gap-2 mt-2">
            <button
              onClick={onClose}
              className="flex-1 py-2 rounded-lg bg-pk-gray-2 border-2 border-pk-gray-3 text-pk-gray-4 font-bold hover:bg-pk-gray-3 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!title.trim() || !content.trim() || isSubmitting}
              className="flex-1 py-2 rounded-lg bg-pk-purple-3 border-2 border-pk-purple-5 text-pk-gray-1 font-bold hover:bg-pk-purple-4 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
